/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { useCurrentBranchQuery } from '~adapters/queries/branch';
import { JIRA_INTEGRATION_ORGANIZATION_UUID } from '~feature-jira/helpers';
import { JiraLinkedWorkItem } from '~feature-jira/index';
import { useJiraProjectBindingQuery } from '~feature-jira/queries';
import { JiraWorkItemResourceType } from '~feature-jira/types';
import { isMainBranch, isPullRequest } from '~shared/helpers/branch-like';
import { isProject } from '~shared/helpers/component';
import { getComponentIssuesUrl, getPathUrlAsString } from '~shared/helpers/urls';
import { useAvailableFeatures } from '~sq-server-commons/context/available-features/withAvailableFeatures';
import { useProjectQuery } from '~sq-server-commons/queries/projects';
import { Feature } from '~sq-server-commons/types/features';
import { Component, Issue } from '~sq-server-commons/types/types';

interface IssueJiraWorkItemProps {
  component: Component;
  issue: Issue;
}

export function IssueJiraWorkItem({ component, issue }: Readonly<IssueJiraWorkItemProps>) {
  const { hasFeature } = useAvailableFeatures();
  const isJiraIntegrationEntitled = hasFeature(Feature.JiraIntegration);

  const { data: branchLike } = useCurrentBranchQuery(component);

  const { data: project } = useProjectQuery(component?.key ?? '', {
    enabled: isJiraIntegrationEntitled && component !== undefined && isProject(component.qualifier),
  });
  const { data: jiraBinding } =
    useJiraProjectBindingQuery(project?.uuid ?? '', {
      enabled:
        project?.uuid !== undefined && (isMainBranch(branchLike) || isPullRequest(branchLike)),
    }) ?? {};

  if (!isJiraIntegrationEntitled || jiraBinding === undefined || project?.uuid === undefined) {
    return null;
  }

  return (
    <JiraLinkedWorkItem
      isDetailsPage={false}
      isEntitled
      isLoadingExternal={false}
      issue={{
        ...issue,
        url: getPathUrlAsString(
          getComponentIssuesUrl(issue.projectKey, { open: issue.key }),
          false,
        ),
        projectUuid: project?.uuid,
      }}
      jiraBinding={jiraBinding}
      orgUUID4={JIRA_INTEGRATION_ORGANIZATION_UUID}
      resourceType={JiraWorkItemResourceType.SonarIssue}
    />
  );
}
